import React from 'react';
import { Form, Input, Modal, message } from 'antd';
import { PatientsFeedback } from 'types/common';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import commonS from 'styles/common.module.less';
import { FormReply } from './types';
import ScoreLabel from 'components/ScoreLabel';
import { getTimeStr } from 'utils/calendar';
import TextArea from 'antd/es/input/TextArea';
import { postReputationFeedbackReply } from 'api/survey';
import DataUpdatedFlagStore from 'store/DataUpdatedFlag';
import Star from 'components/Star';
import { getFullName } from 'utils/common';

type Props = {
    show:boolean,
    item?:PatientsFeedback,
    onCannel?: ()=>void,
    onSuccess?: ()=>void
};

const ReplyFeedbackModal = (props: Props) => {
    const { show, item, onCannel, onSuccess } = props;
    const [submiting, setSubmiting] = React.useState(false);
    const [form] = Form.useForm<FormReply>();
    const [, setDataUpdatedFlag] = DataUpdatedFlagStore.useStore();

    React.useEffect(() => {
        if (!show) {
            form.resetFields();
        }
    }, [show]);

    const onSubmit = async () => {
        try {
            const formValues = await form.validateFields();

            const data = {
                ...formValues,
                answerId: item?.id!,
            };

            setSubmiting(true);

            const result = await postReputationFeedbackReply(data);
            if (!result.error) {
                message.success('Reply success');
                setDataUpdatedFlag('patientFeedbackListUpdated', {});
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (e:any) {
            if (e.errorFields) {
                //form error
                return;
            }
            //const msg = e?.toString?.() || 'data error';
            //message.error(msg);
        } finally {
            setSubmiting(false);
        }
    };

    return (
        <Modal
            title="Reply feedback"
            closable
            className={commonS.modalFixHeightWrap}
            width="1100px"
            onCancel={() => {
                onCannel?.();
            }}
            okText="Send"
            open={show}
            onOk={onSubmit}
            destroyOnClose
            okButtonProps={{ loading: submiting }}
            cancelButtonProps={{ style: { display: 'none' } }}
        >
            <div className={s.wrap}>
                <div className={s.feedbackItem} key={`${item?.firstName} ${item?.lastName}`}>
                    <div className={s.left}>
                        <div className={s.info}>
                            <div className={s.nameAndScore}>
                                <div className={s.name}>{getFullName(item?.firstName, item?.lastName)} {item?.source && <span className={s.source}>{`| ${item?.source}`}</span>}</div>
                            </div>
                            {
                                (item?.email || item?.phoneNumber) && (
                                    <div className={s.emailAndPhone}>
                                        {
                                            item?.email && (
                                                <div className={s.email}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M2 5.33398L7.2604 8.84092C7.70827 9.1395 8.29173 9.1395 8.7396 8.84092L14 5.33398M3.33333 12.6673H12.6667C13.403 12.6673 14 12.0704 14 11.334V4.66732C14 3.93094 13.403 3.33398 12.6667 3.33398H3.33333C2.59695 3.33398 2 3.93094 2 4.66732V11.334C2 12.0704 2.59695 12.6673 3.33333 12.6673Z" stroke="#9CA3AF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                    {item?.email}
                                                </div>
                                            )
                                        }
                                        {
                                            item?.phoneNumber && (
                                                <div className={s.phone}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M6.15195 2.45585L5.51949 2.66667V2.66667L6.15195 2.45585ZM7.15049 5.45147L7.78295 5.24066L7.78295 5.24066L7.15049 5.45147ZM5.31134 7.011L5.0132 6.41471C4.69317 6.57473 4.55652 6.95883 4.70359 7.28501L5.31134 7.011ZM8.989 10.6887L8.71499 11.2964C9.04117 11.4435 9.42527 11.3068 9.58529 10.9868L8.989 10.6887ZM9.74142 9.18382L9.14514 8.88568L9.14514 8.88568L9.74142 9.18382ZM10.5485 8.84951L10.7593 8.21705L10.5485 8.84951ZM13.5442 9.84805L13.3333 10.4805L13.5442 9.84805ZM2 4.00065V3.33398C1.82318 3.33398 1.65361 3.40423 1.52858 3.52926C1.40355 3.65429 1.33332 3.82387 1.33333 4.00069L2 4.00065ZM2.00052 4.00065V4.66732C2.25679 4.66732 2.49038 4.52042 2.60139 4.28944C2.7124 4.05847 2.68119 3.7843 2.5211 3.58419L2.00052 4.00065ZM2 4H1.33333C1.33333 4.15138 1.38485 4.29825 1.47942 4.41646L2 4ZM3.33333 1.33333C2.22876 1.33333 1.33333 2.22876 1.33333 3.33333H2.66667C2.66667 2.96514 2.96514 2.66667 3.33333 2.66667V1.33333ZM5.51949 1.33333H3.33333V2.66667H5.51949V1.33333ZM6.78441 2.24503C6.60292 1.70057 6.0934 1.33333 5.51949 1.33333V2.66667V2.66667L6.78441 2.24503ZM7.78295 5.24066L6.7844 2.24503L5.51949 2.66667L6.51804 5.66229L7.78295 5.24066ZM7.11432 6.85486C7.7078 6.55812 7.99277 5.87014 7.78295 5.24066L6.51804 5.66229V5.66229L7.11432 6.85486ZM5.60948 7.60728L7.11432 6.85486L6.51804 5.66229L5.0132 6.41471L5.60948 7.60728ZM9.26302 10.0809C7.78142 9.41291 6.58709 8.21858 5.91909 6.73698L4.70359 7.28501C5.50524 9.06305 6.93695 10.4948 8.71499 11.2964L9.26302 10.0809ZM9.14514 8.88568L8.39272 10.3905L9.58529 10.9868L10.3377 9.48196L9.14514 8.88568ZM10.7593 8.21705C10.1299 8.00723 9.44188 8.2922 9.14514 8.88568L10.3377 9.48197V9.48196L10.7593 8.21705ZM13.755 9.2156L10.7593 8.21705L10.3377 9.48196L13.3333 10.4805L13.755 9.2156ZM14.6667 10.4805C14.6667 9.9066 14.2994 9.39708 13.755 9.2156L13.3333 10.4805H13.3333H14.6667ZM14.6667 12.6667V10.4805H13.3333V12.6667H14.6667ZM12.6667 14.6667C13.7712 14.6667 14.6667 13.7712 14.6667 12.6667H13.3333C13.3333 13.0349 13.0349 13.3333 12.6667 13.3333V14.6667ZM12 14.6667H12.6667V13.3333H12V14.6667ZM1.33333 4.00069C1.33371 9.89141 6.10919 14.6667 12 14.6667V13.3333C6.84554 13.3333 2.66699 9.15499 2.66667 4.00061L1.33333 4.00069ZM2.00052 3.33398H2V4.66732H2.00052V3.33398ZM1.47942 4.41646L1.47994 4.41711L2.5211 3.58419L2.52058 3.58354L1.47942 4.41646ZM1.33333 3.33333V4H2.66667V3.33333H1.33333Z" fill="#9CA3AF" />
                                                    </svg>
                                                    {item?.phoneNumber}
                                                </div>
                                            )
                                        }
                                    </div>
                                )
                            }
                            <div className={s.contentAndTime}>
                                <div className={s.content}>{item?.feedbackContent}</div>
                            </div>
                        </div>
                        <div className={s.starNDate}>
                            <div className={s.score}><Star yellow width={80} gap={0.2} value={item?.overallScore} /></div>
                            <div className={s.time}>{getTimeStr(dayjs(item?.answerAt), 'MMMM D, YYYY')}</div>
                        </div>
                    </div>
                </div>
                <Form
                    form={form}
                    name="basic"
                    className={commonS.formStyle1}
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    layout="vertical"
                    scrollToFirstError
                >
                    <div className={s.modalWrap}>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '700px' }}
                                    label="Subject"
                                    name="subject"
                                    rules={[
                                        { required: true, message: 'Subject is required' },
                                        { max: 100, message: 'Subject maximum character length of 100' },
                                    ]}
                                >
                                    <Input maxLength={100} placeholder="" />
                                </Form.Item>
                            </div>
                        </div>

                        <div className={s.maxTips}>Max. 3000 characters</div>
                        <div className={s.row} style={{ marginTop: '-24px' }}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '700px' }}
                                    label="Message"
                                    name="content"
                                    rules={[
                                        { required: true, message: 'Message is required' },
                                        { max: 3000, message: 'Message maximum character length of 3000' },
                                    ]}
                                >
                                    <TextArea rows={6} placeholder="" maxLength={3000} />
                                </Form.Item>
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        </Modal>
    );
};

export default ReplyFeedbackModal;
